import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHead } from '../../design-system/composites/Card/Card';
import { SwapIcon } from '@phosphor-icons/react/dist/csr/Swap';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { Button } from '../../design-system/primitives/Button/Button';
import { replacementsApi, type ReplacementRow } from '../../lib/api/replacements';
import { DEVICE_TYPE_LABEL_KEY, STATUS_LABEL_KEY } from '../replacements/replacementLabels';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './CaseReplacements.module.css';

/**
 * The Replacements panel on the case detail screen (M-13, Task 6, spec §5)
 * — beside M-08's `CaseMessages`, M-09's `CaseRequests` and M-10's
 * `CaseEscalations`, the identical shape: gated on its own permission, its
 * own error state, no spinner that never resolves.
 *
 * ## `status: 'all'` IS LOAD-BEARING, AND THIS IS THE THIRD TIME
 *
 * The server's default is `requested` — the work queue. A panel that took
 * it would show only the OUTSTANDING replacements for this case and hide
 * every one already dispensed or rejected, which is precisely the history a
 * case record exists to carry: "what did we give this patient, and when".
 *
 * M-09's identical panel shipped that defect (it took the requests queue's
 * `open` default and rendered "no requests" for every case on a live
 * system), M-10's nearly did, and `CaseEscalations.tsx`'s own docblock
 * records both. This panel therefore always passes `status: 'all'`,
 * verified by a test asserting the OUTGOING QUERY, not merely that a row
 * happens to be present — the fixtures ignore the query, so a row-presence
 * assertion would pass against a panel that sent nothing at all.
 *
 * ## Filtered by `case_number`, not the case's `uuid`
 *
 * `ReplacementIndexRequest::rules()` (verified against the API repository)
 * carries a `case_number` filter and no `case_uuid` filter at all — the
 * identical shape `CaseRequests.tsx` and `CaseEscalations.tsx` document.
 * `CaseDetail.tsx` already holds the case's own `case_number`, unique per
 * case.
 *
 * ## Read-only, deliberately
 *
 * No dispense or reject controls here — `ReplacementDetail.tsx` is where
 * those live, gated on the record's OWN `can` shape rather than re-derived
 * a second time on this surface. Two copies of one gating rule is how
 * `escalation.view` hid a feature three times in one milestone. This panel
 * answers one question: which replacements concern this case, and where
 * each currently stands.
 */

export interface CaseReplacementsProps {
  caseNumber: string;
  canView: boolean;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function CaseReplacements({ caseNumber, canView }: CaseReplacementsProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [rows, setRows] = useState<readonly ReplacementRow[] | null>(null);

  const loadReplacements = useCallback(async () => {
    setStatus('loading');

    try {
      // `status: 'all'` IS LOAD-BEARING — see this module's own docblock.
      const page = await replacementsApi.list({
        case_number: caseNumber,
        status: 'all',
        per_page: 200,
      });
      setRows(page.data);
      setStatus('ready');
    } catch {
      // This panel's own error state, not `CaseDetail`'s.
      setStatus('error');
    }
  }, [caseNumber]);

  useEffect(() => {
    if (!canView) return;
    void loadReplacements();
  }, [canView, loadReplacements]);

  if (!canView) return null;

  return (
    <section className={styles['panel']}>
      <CardHead level="h3" icon={<SwapIcon size={16} weight="duotone" />} title={t('replacements.panelTitle')} />

      {status === 'error' ? (
        <div role="alert" className={styles['empty']}>
          <p>{t('replacements.panelLoadFailed')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void loadReplacements();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('cases.retry')}
          </Button>
        </div>
      ) : rows === null ? (
        <p role="status">{t('list.loading')}</p>
      ) : rows.length === 0 ? (
        <p className={styles['empty']}>{t('replacements.panelEmpty')}</p>
      ) : (
        <ul className={styles['list']}>
          {rows.map((replacement) => (
            <li key={replacement.uuid} className={styles['row']}>
              <div className={styles['rowHeader']}>
                <span className={styles['serial']}>{t(DEVICE_TYPE_LABEL_KEY[replacement.device_type])}</span>
                <span className={styles['statusLabel']}>{t(STATUS_LABEL_KEY[replacement.status])}</span>
              </div>

              <div className={styles['meta']}>
                {replacement.old_serial_number === null ? null : (
                  <span>
                    {t('replacements.fieldOldSerial')}: <bdi dir="ltr">{replacement.old_serial_number}</bdi>
                  </span>
                )}
                {/* WHAT THE PATIENT WAS ACTUALLY GIVEN — the one fact the
                    legacy recorded nowhere at all (spec R-4). */}
                {replacement.new_device === null ? null : (
                  <span>
                    {t('replacements.fieldNewDevice')}:{' '}
                    <bdi dir="ltr">{replacement.new_device.serial_number}</bdi>
                  </span>
                )}
                {replacement.requested_at === null ? null : (
                  <span>
                    {t('replacements.fieldRequestedAt')}:{' '}
                    <bdi dir="ltr">{formatTimestamp(replacement.requested_at, i18n.language)}</bdi>
                  </span>
                )}
              </div>

              {replacement.rejection_reason === null ? null : (
                <p className={styles['reason']}>{replacement.rejection_reason}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
